import React, { useState, useEffect, useMemo } from 'react';
import {
  Plus,
  ChevronDown,
  ChevronUp,
  Search,
  Loader,
  X,
  Pencil
} from 'lucide-react';
import apiService from './services/api';
import { Panel, Button, Badge, LoadingSpinner } from './components';

function ServicesPage() {
  const isCoordinator = localStorage.getItem('user_role') === 'coordinator';
  // State
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [onlineBookingFilter, setOnlineBookingFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const itemsPerPage = 15;

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    square_id: '',
    active: true,
    is_bookable_online: false,
    split_type: '',
    split_percentage: '',
    split_amount: ''
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Fetch services from API on component mount
  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      setError(null);

      // Call API
      const data = await apiService.getServices();

      setServices(data.results || data);
    } catch (err) {
      setError(err.message || 'Failed to load services');
      console.error('Error loading services:', err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (service = null) => {
    if (service) {
      // Edit existing service
      setEditingService(service);
      setFormData({
        name: service.name || '',
        price: service.price || '',
        square_id: service.square_id || '',
        active: service.active !== undefined ? service.active : true,
        is_bookable_online: service.is_bookable_online || false,
        split_type: service.split_type || '',
        split_percentage: service.split_percentage || '',
        split_amount: service.split_amount || ''
      });
    } else {
      // Create new service
      setEditingService(null);
      setFormData({
        name: '',
        price: '',
        square_id: '',
        active: true,
        is_bookable_online: false,
        split_type: '',
        split_percentage: '',
        split_amount: ''
      });
    }
    setSaveError(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingService(null);
    setFormData({
      name: '',
      price: '',
      square_id: '',
      active: true,
      is_bookable_online: false,
      split_type: '',
      split_percentage: '',
      split_amount: ''
    });
    setSaveError(null);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    try {
      setSaveLoading(true);
      setSaveError(null);

      // Validate
      if (!formData.name || !formData.name.trim()) {
        setSaveError('Service name is required');
        return;
      }

      if (!formData.price || isNaN(parseFloat(formData.price))) {
        setSaveError('Valid price is required');
        return;
      }

      const payload = {
        name: formData.name.trim(),
        price: parseFloat(formData.price),
        square_id: formData.square_id?.trim() || null,
        active: formData.active,
        is_bookable_online: formData.is_bookable_online,
        split_type: formData.split_type || null,
        split_percentage: formData.split_percentage ? parseFloat(formData.split_percentage) : null,
        split_amount: formData.split_amount ? parseFloat(formData.split_amount) : null
      };

      if (editingService) {
        // Update existing service
        await apiService.updateService(editingService.id, payload);
      } else {
        // Create new service
        await apiService.createService(payload);
      }

      // Reload services and close modal
      await loadServices();
      closeModal();
    } catch (err) {
      setSaveError(err.message || 'Failed to save service');
      console.error('Error saving service:', err);
    } finally {
      setSaveLoading(false);
    }
  };

  // Filter and sort data locally
  const filteredAndSortedData = useMemo(() => {
    let filtered = services.filter(service => {
      const matchesSearch =
        service.name?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesActive = activeFilter === 'all' ||
        (activeFilter === 'active' && service.active) ||
        (activeFilter === 'inactive' && !service.active);

      const matchesOnlineBooking = onlineBookingFilter === 'all' ||
        (onlineBookingFilter === 'visible' && service.is_bookable_online) ||
        (onlineBookingFilter === 'hidden' && !service.is_bookable_online);

      return matchesSearch && matchesActive && matchesOnlineBooking;
    });

    // Sort data
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'price' || sortField === 'duration_minutes') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [services, searchTerm, activeFilter, onlineBookingFilter, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredAndSortedData.slice(startIndex, endIndex);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Loading state
  if (loading && services.length === 0) {
    return (
      <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
        <div className="p-3">
          <Panel className="p-6">
            <LoadingSpinner />
          </Panel>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
        <div className="p-3">
          <Panel className="bg-red-50 border-red-200">
            <h3 className="text-xs font-semibold text-red-800">Error Loading Services</h3>
            <p className="text-xs text-red-600 mt-1">{error}</p>
            <Button onClick={loadServices} size="sm" className="mt-2 bg-red-600 hover:bg-red-700">
              Retry
            </Button>
          </Panel>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-neutral-50 p-3 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      {/* Page Title */}
      <div className="mb-3">
        <h1 className="text-lg font-semibold text-gray-900">SERVICES</h1>
        <p className="text-xs text-gray-600 mt-0.5">{filteredAndSortedData.length} services found</p>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm p-3 mb-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
            <input
              type="text"
              placeholder="Search services..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800 focus:border-transparent"
            />
          </div>

          {/* Active Filter */}
          <select
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
            className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
          >
            <option value="all">All Services</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>

          {/* Online Booking Filter */}
          <select
            value={onlineBookingFilter}
            onChange={(e) => setOnlineBookingFilter(e.target.value)}
            className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
          >
            <option value="all">All (Online Booking)</option>
            <option value="visible">Visible Online</option>
            <option value="hidden">Hidden Online</option>
          </select>
        </div>
      </div>

      {/* Services Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th
                  className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Service Name</span>
                    {sortField === 'name' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th
                  className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('price')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Price</span>
                    {sortField === 'price' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </div>
                </th>
                <th
                  className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('duration_minutes')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Duration</span>
                    {sortField === 'duration_minutes' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                    )}
                  </div>
                </th>
                {!isCoordinator && (<>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Split Type</th>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Split %</th>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Split $</th>
                </>)}
                <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Online Booking</th>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentData.map((service) => (
                <tr key={service.id} className="hover:bg-gray-50">
                  <td className="px-2 py-1.5 text-xs text-gray-900 font-medium">{service.name}</td>
                  <td className="px-2 py-1.5 text-xs text-gray-900 font-semibold">{formatCurrency(service.price)}</td>
                  <td className="px-2 py-1.5 text-xs text-gray-600">
                    {service.duration_minutes ? `${service.duration_minutes} min` : '-'}
                  </td>
                  {!isCoordinator && (<>
                  <td className="px-2 py-1.5 text-xs text-gray-600">
                    {service.split_type === 'percentage' ? 'Percentage' : service.split_type === 'fixed' ? 'Fixed' : '-'}
                  </td>
                  <td className="px-2 py-1.5 text-xs text-gray-600">
                    {service.split_percentage ? `${service.split_percentage}%` : '-'}
                  </td>
                  <td className="px-2 py-1.5 text-xs text-gray-600">
                    {service.split_amount ? formatCurrency(service.split_amount) : '-'}
                  </td>
                  </>)}
                  <td className="px-2 py-1.5 text-xs">
                    <Badge variant="neutral" small>
                      {service.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-2 py-1.5 text-xs">
                    <Badge variant={service.is_bookable_online ? 'success' : 'neutral'} small>
                      {service.is_bookable_online ? 'Visible' : 'Hidden'}
                    </Badge>
                  </td>
                  <td className="px-2 py-1.5 text-xs">
                    <button
                      onClick={() => openModal(service)}
                      className="text-gray-400 hover:text-red-800 transition-colors"
                      title="Edit Service"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-2 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-700">
              Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
              <span className="font-medium">{Math.min(endIndex, filteredAndSortedData.length)}</span> of{' '}
              <span className="font-medium">{filteredAndSortedData.length}</span> results
            </p>
          </div>
          <div className="flex gap-1">
            <Button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              variant={currentPage === 1 ? 'secondary' : 'primary'}
              size="sm"
            >
              Previous
            </Button>
            {[...Array(Math.min(totalPages, 5))].map((_, index) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = index + 1;
              } else if (currentPage <= 3) {
                pageNum = index + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + index;
              } else {
                pageNum = currentPage - 2 + index;
              }
              if (pageNum > totalPages) return null;
              return (
                <Button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  variant={currentPage === pageNum ? 'primary' : 'secondary'}
                  size="sm"
                >
                  {pageNum}
                </Button>
              );
            })}
            <Button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              variant={currentPage === totalPages ? 'secondary' : 'primary'}
              size="sm"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Floating Add Button */}
      <button
        onClick={() => openModal()}
        className="fixed bottom-6 right-6 w-12 h-12 bg-red-800 rounded-full flex items-center justify-center shadow-lg hover:bg-red-900 transition-colors"
        title="Add Service"
      >
        <Plus className="w-5 h-5 text-white" />
      </button>

      {/* Edit/Create Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-3 border-b border-gray-200">
              <h2 className="text-sm font-semibold text-gray-900">
                {editingService ? 'Edit Service' : 'New Service'}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-3 space-y-3">
              {saveError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                  <p className="text-xs text-red-600">{saveError}</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Service Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                  placeholder="e.g., Full Set - Acrylic"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Price <span className="text-red-600">*</span>
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleFormChange}
                  step="0.01"
                  min="0"
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                  placeholder="0.00"
                />
              </div>

              {/* Split Configuration */}
              <div className="border-t border-gray-200 pt-3 mt-3">
                <h3 className="text-xs font-semibold text-gray-700 mb-2">Payroll Split (for team member payout)</h3>

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Split Type
                    </label>
                    <select
                      name="split_type"
                      value={formData.split_type}
                      onChange={handleFormChange}
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                    >
                      <option value="">Select...</option>
                      <option value="percentage">Percentage</option>
                      <option value="fixed">Fixed Amount</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Split %
                    </label>
                    <input
                      type="number"
                      name="split_percentage"
                      value={formData.split_percentage}
                      onChange={handleFormChange}
                      step="0.01"
                      min="0"
                      max="100"
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                      placeholder="e.g., 50"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Split $
                    </label>
                    <input
                      type="number"
                      name="split_amount"
                      value={formData.split_amount}
                      onChange={handleFormChange}
                      step="0.01"
                      min="0"
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                      placeholder="e.g., 75.00"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Square ID
                </label>
                <input
                  type="text"
                  name="square_id"
                  value={formData.square_id}
                  onChange={handleFormChange}
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                  placeholder="Optional Square service ID"
                  disabled={!!editingService?.square_id}
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="active"
                  id="active"
                  checked={formData.active}
                  onChange={handleFormChange}
                  className="w-4 h-4 text-red-800 border-gray-300 rounded focus:ring-red-800"
                />
                <label htmlFor="active" className="ml-2 text-sm text-gray-700">
                  Active Service
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_bookable_online"
                  id="is_bookable_online"
                  checked={formData.is_bookable_online}
                  onChange={handleFormChange}
                  className="w-4 h-4 text-red-800 border-gray-300 rounded focus:ring-red-800"
                />
                <label htmlFor="is_bookable_online" className="ml-2 text-sm text-gray-700">
                  Show on Online Booking
                </label>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-2 p-3 border-t border-gray-200">
              <Button
                variant="secondary"
                size="md"
                onClick={closeModal}
                disabled={saveLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSave}
                disabled={saveLoading}
              >
                {saveLoading ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingService ? 'Update' : 'Create'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ServicesPage;
